/**
 * Collection Vue Component
 * ------------------------------------------------------------------------------
 * Template in sections/collection.liquid
 *
 * Sort functionality:
 * Using hidden input for sort field to concentrate filtering <form> to required
 * elements only, to avoid nested forms (product forms).
 */

import axios from 'axios'
import Vue from 'vue'
import Cookies from 'js-cookie'

import ProductItem from '@/js/components/product-item.js'
import VueCollapsible from '@/js/components/vue-collapsible.vue'
import { transformGraphqlProduct, collectionProductsRequest, searchProductsRequest } from '@/js/utils/data'
import { storefrontRequestConfig, STOREFRONT_URL } from '@/js/utils/api'

const SHOPIFY_SORT_OPTIONS = [
  {
    shopify_key: 'MANUAL',
    label_key: 'manual',
    sort_by: 'manual',
    dir: 'asc',
    reverse: false,
    value: 'Featured'
  },
  {
    shopify_key: 'TITLE',
    label_key: 'title-ascending',
    sort_by: 'title',
    dir: 'asc',
    reverse: false,
    value: 'Alphabetically, A-Z'
  },
  {
    shopify_key: 'TITLE',
    label_key: 'title-descending',
    sort_by: 'title',
    dir: 'desc',
    reverse: true,
    value: 'Alphabetically, Z-A'
  },
  {
    shopify_key: 'PRICE',
    label_key: 'price-ascending',
    sort_by: 'price',
    dir: 'asc',
    reverse: false,
    value: 'Price, low to high'
  },
  {
    shopify_key: 'PRICE',
    label_key: 'price-descending',
    sort_by: 'price',
    dir: 'desc',
    reverse: true,
    value: 'Price, high to low'
  },
  {
    shopify_key: 'CREATED',
    label_key: 'created-ascending',
    sort_by: 'created_at',
    dir: 'asc',
    reverse: false,
    value: 'Date, old to new'
  },
  {
    shopify_key: 'CREATED',
    label_key: 'created-descending',
    sort_by: 'created_at',
    dir: 'desc',
    reverse: true,
    value: 'Date, new to old'
  }
]

const SHOPIFY_SEARCH_SORT_OPTIONS = [
  {
    shopify_key: 'RELEVANCE',
    label_key: 'relevance',
    sort_by: 'relevance',
    dir: 'asc',
    reverse: false,
    value: 'Relevance'
  },
  {
    shopify_key: 'PRICE',
    label_key: 'price-ascending',
    sort_by: 'price',
    dir: 'asc',
    reverse: false,
    value: 'Price, low to high'
  },
  {
    shopify_key: 'PRICE',
    label_key: 'price-descending',
    sort_by: 'price',
    dir: 'desc',
    reverse: true,
    value: 'Price, high to low'
  }
]

export default {
  components: {
    ProductItem,
    VueCollapsible
  },

  props: {
    collection: {
      type: Object,
      required: true
    },
    originalProducts: {
      type: Array
    },
    defaultSort: {
      type: String,
      required: true
    },
    isSearchPage: {
      type: Boolean,
      required: true
    },
    sortOptionLabels: {
      type: Object,
      required: true
    },
    searchQuery: {
      type: String
    },
    productCount: {
      type: Number
    }
  },

  data () {
    return {
      isLoading: false,
      productsToDisplay: [],
      productsPerPage: 16,
      currentPage: 1,
      gridLayout: 'normal',
      // more,normal,less
      showMobileFilter: false,
      showSortMenu: false,

      isLoadingMore: false,
      productsLoaded: false,
      products: [], // filtered and based on pagination
      allProducts: [], // unfiltered
      paginateBy: 16,
      hasNextPage: false,
      showLoadMore: false,
      resultsCount: null,
      filteredResultsCount: null,
      lastProductCursor: null,
      filterData: [],
      filterQuery: [],
      originalQuery: [],
      selectedFilters: {},
      activeFilters: [],
      activeQuery: null,
      activeDropdownMenu: null,
      activeSort: null,
      sortBy: null,
      sortOptions: [],
      sortValue: '',
      isResultsUpdating: false,
      previewHasNextPage: null,
      previewFilterQuery: [],
      previewLastProductCursor: null,
      previewAllProducts: []
    }
  },

  computed: {
    isFiltersDisabled () {
      return !this.products.length && !Object.values(this.selectedFilters).length
    },

    numOfProducts () {
      return this.products.length
    },

    currentState () {
      return Cookies.get('state') || 'NSW' // used to filter products based on the available inventory in a location
    }
  },

  watch: {
    selectedFilterValue (id) {
      return this.selectedFilters[id] ? JSON.parse(this.selectedFilters[id]).map(item => item.productMetafield.value) : ''
    }
  },

  mounted () {
    this._setValuesFromUrlParams()

    this.resultsCount = this.productCount // update product count

    window.onpopstate = () => {
      // Reload if navigated from browser history (so pagination works)
      this.isLoadingFromHistory = true
      this.currentPage = 1
      this._setValuesFromUrlParams()
    }

    if (this.isSearchPage) {
      for (const option of SHOPIFY_SEARCH_SORT_OPTIONS) {
        this.sortOptions.push(option)
      }
    } else {
      for (const option of SHOPIFY_SORT_OPTIONS) {
        this.sortOptions.push(option)
      }
    }

    // Initialize sortValue and sortBy if not set from URL params
    if (!this.sortValue) {
      if (this.sortBy) {
        // If sortBy was set from URL params, use its label_key
        this.sortValue = this.sortBy.label_key
        this.activeSort = this.sortBy.value
      } else if (this.defaultSort) {
        // If no URL params, initialize from defaultSort prop
        this.sortBy = this.isSearchPage
          ? SHOPIFY_SEARCH_SORT_OPTIONS.find(option => option.label_key === this.defaultSort)
          : SHOPIFY_SORT_OPTIONS.find(option => option.label_key === this.defaultSort)
        if (this.sortBy) {
          this.sortValue = this.sortBy.label_key
          this.activeSort = this.sortBy.value
        }
      }
    }

    // Fallback to first option only if neither URL params nor defaultSort were used
    if (!this.sortBy) {
      this.sortBy = this.isSearchPage ? SHOPIFY_SEARCH_SORT_OPTIONS[0] : SHOPIFY_SORT_OPTIONS[0]
      if (!this.sortValue) {
        this.sortValue = this.sortBy.label_key
        this.activeSort = this.sortBy.value
      }
    }

    document.addEventListener('click', this.handleClickOutside)

    // Data reference for the liquid generated products
    // console.log('Original products:', this.originalProducts)
    // console.log('sortBy', this.sortBy)
    // console.log('sortOptions', this.sortOptions)
    // console.log('sortValue', this.sortValue)
    // console.log('selectedFilters', this.selectedFilters)
    // console.log('filterData', this.filterData)
    // console.log('filters', this.filters)
  },

  created () {
    window.addEventListener('scroll', this.handleScroll)
  },

  beforeUnmount () {
    document.removeEventListener('click', this.handleClickOutside)
  },

  unmounted () {
    window.removeEventListener('scroll', this.handleScroll)
  },

  methods: {
    toggleActiveDropdownMenu (index) {
      this.activeDropdownMenu = index === this.activeDropdownMenu ? null : index
    },

    toggleNewsletter (productCount = 0) {
      const newsletter = document.querySelector('.newsletter-section')
      const productList = document.querySelector('.product-list-and-filtering')

      if (newsletter && productCount < parseInt(newsletter.dataset.threshold)) {
        newsletter.classList.remove('hidden')
      }

      if (productList && productCount < 1) {
        productList.classList.add('hidden')
      }
    },

    handleClickOutside (event) {
      // hides the displayed dropdown menu
      if (!this.$refs.collection__filter__group.contains(event.target)) {
        this.activeDropdownMenu = null
      }
      if (!this.$refs.collection__sort__group.contains(event.target)) {
        this.showSortMenu = false
      }
    },

    submitFilters (event) {
      event.preventDefault()
      this.showMobileFilter = false
      this.getProducts(true)
    },

    setGridLayout (value) {
      this.gridLayout = value
    },

    toggleMobileFilter () {
      this.showMobileFilter = !this.showMobileFilter
    },

    updateSort (sortValue) {
      this.sortValue = sortValue
      this.sortBy = this.isSearchPage ? SHOPIFY_SEARCH_SORT_OPTIONS?.find((sort) => sort.label_key === this.sortValue) : SHOPIFY_SORT_OPTIONS?.find((sort) => sort.label_key === this.sortValue)
      this.activeSort = this.sortBy?.value

      // On desktop, close menu and apply sort immediately
      // On mobile, keep menu open and wait for APPLY button
      if (window.matchMedia('(min-width: 1280px)').matches) {
        this.showSortMenu = !this.showSortMenu
        this.getProducts(true)
      }
      // On mobile, sort will be applied when user clicks APPLY button (via submitFilters)
    },

    autoSubmitFilter (event) {
      const filterKey = event.target.name
      const filterValue = event.target.value
      this.updateFilter(filterKey, filterValue)
      this.getProducts(true)
    },

    updateFilter (filterKey, filterValue) {
      if (filterValue && filterValue !== '') {
        if (this.selectedFilters[filterKey]?.includes(filterValue)) {
          this.selectedFilters[filterKey] = this.selectedFilters[filterKey].filter(option => option !== filterValue)
        } else {
          this.selectedFilters[filterKey].push(filterValue)
        }
      } else {
        this.selectedFilters[filterKey] = []
      }

      this.getFilteredProducts(true) // perform Storefront API call to get a preview of the filtered products (will not update the product items, this is only used for getting the expected result count when checking a filter option)
    },

    handleFilterSelect (event) {
      const filterKey = event.target.name
      const filterValue = event.target.value
      if (filterValue && filterValue !== '') {
        if (this.selectedFilters[filterKey]?.includes(filterValue)) {
          this.selectedFilters[filterKey] = this.selectedFilters[filterKey].filter(option => option !== filterValue)
        } else {
          this.selectedFilters[filterKey].push(filterValue)
        }
      } else {
        this.selectedFilters[filterKey] = []
      }
      this.getProducts(true)
    },

    handleResetFilters (hideMobileFilter = false) {
      for (const filter of this.filterData) {
        this.selectedFilters[filter.id] = []
      }
      this.activeDropdownMenu = null

      if (hideMobileFilter) {
        this.showMobileFilter = false
      }
      this.getProducts(true)
    },

    _updateUrlParams () {
      // Set URL params to persist filter selections

      // Filters
      const filtersArray = []
      for (const filter of this.filterData) {
        if (this.selectedFilters[filter.id]?.length) {
          const filterKey = filter.id.split('.').pop()

          for (const item of this.activeQuery) {
            if (item.productMetafield && item.productMetafield.key === filterKey) {
              // Encode filter value for URL (e.g., $500-$1000 becomes %24500-%241000)
              const encodedValue = encodeURIComponent(item.productMetafield.value)
              filtersArray.push(`${filter.id}=${encodedValue}`)
            }
          }
        }
      }

      // Reference for showing clear filter, this variable checks for active filters
      this.activeFilters = filtersArray ? filtersArray.filter(filter => !filter.includes('sort_by')) : []

      // Sort
      if (this.sortBy && this.sortBy.label_key !== this.defaultSort) {
        filtersArray.push(`sort_by=${this.sortBy.label_key}`)
      }

      // Build URL query strings
      let url = window.location.origin + window.location.pathname
      const paramsArray = filtersArray?.map((element) => {
        return element
      }).join('&')

      if (paramsArray) {
        url += `?${paramsArray.replaceAll(' ', '+')}`
      }

      if (Cookies.get('postal_code') && paramsArray) {
        url += `&postCode=${Cookies.get('postal_code')}`
      } else {
        url += `?postCode=${Cookies.get('postal_code')}`
      }

      if (!this.isLoadingFromHistory) window.history.pushState({}, '', url)
    },

    async getProducts (triggerFilter = false) {
      if (!this.sortBy) {
      // Default to "Featured" (manual) for collections
        this.sortBy = this.isSearchPage ? SHOPIFY_SEARCH_SORT_OPTIONS[0] : SHOPIFY_SORT_OPTIONS[0]
        this.sortValue = this.sortBy?.label_key
      }

      // If defaultSort prop is provided and matches "manual", ensure Featured is selected
      if (this.defaultSort === 'manual' && !this.sortValue) {
        const featuredOption = SHOPIFY_SORT_OPTIONS.find(option => option.sort_by === 'manual')
        if (featuredOption) {
          this.sortBy = featuredOption
          this.sortValue = featuredOption.label_key
        }
      }

      // reset product array, page, and product cursor (graphQL product index)
      if (triggerFilter) {
        this.products = []
        this.allProducts = [] // reset all product list
        this.lastProductCursor = null
        this.currentPage = 1
      }

      try {
        // if 1st fetched products are loaded no need to show loading animation
        if (!this.productsLoaded) {
          this.isLoading = true
        }

        // generate filter query
        if (this.filterData && this.filterData.length > 0) {
          for (const filter of this.filterData) {
            if (this.selectedFilters[filter.id]?.length) {
              if (filter.id !== 'filter.v.price') {
                const filterArray = this.selectedFilters[filter.id]
                for (const arrayElement of filterArray) {
                  this.filterQuery = this.filterQuery.concat(JSON.parse(`${arrayElement}`))
                }
              }
            }
          }
        }

        if (!this.originalQuery) {
          this.originalQuery = this.filterQuery
        }

        // start of GraphQL query
        let productArguments = `first: 250, sortKey: ${this.sortBy.shopify_key}, reverse: ${this.sortBy.reverse}`

        // apply filter query
        if (this.filterQuery && this.filterQuery.length > 0) {
          let filterValues = ''
          for (const [index, query] of this.filterQuery.entries()) {
            filterValues += `${JSON.stringify(query).replace(/"([^"]+)":/g, '$1:')}`
            if (index !== this.filterQuery.length - 1) {
              filterValues += ','
            }
          }

          productArguments = this.isSearchPage ? productArguments + `, productFilters: [${filterValues}]` : productArguments + `, filters: [${filterValues}]`
        }

        // add last product index (for next page), sort value must be the same and there are no changes in the selected filters
        if (this.lastProductCursor) {
          productArguments = productArguments + `, after: "${this.lastProductCursor}"`
        }

        const metarequestBody = this.isSearchPage ? searchProductsRequest(this.searchQuery, productArguments) : collectionProductsRequest(this.collection.handle, productArguments)

        await axios.post(STOREFRONT_URL, metarequestBody, storefrontRequestConfig).then(async ({ data, config }) => {
          const productsFromShopify = this.isSearchPage ? data.data.search.edges : data.data.collection.products.edges
          this.hasNextPage = this.isSearchPage ? data.data.search.pageInfo?.hasNextPage : data.data.collection.products.pageInfo?.hasNextPage // this is true if collection has more than 250 products
          this.productsLoaded = true // reference variable for 1st result

          productsFromShopify.forEach((product, key, array) => {
            let productItem = {}
            // let siblingCode = product.node.sibling_code.value;

            // format product item
            productItem = transformGraphqlProduct(product.node)

            // check if the current state value is ACT
            if (this.currentState === 'ACT') {
              // If the state is ACT, include products from both ACT and NSW warehouses
              if (productItem.available_on?.includes(this.currentState) || productItem.available_on?.includes('NSW')) {
                // set the formatted product
                this.allProducts.push(productItem)
              }
            } else {
              // check the state value (from the Cookie) and match if the selected state has an available inventory in the product
              if (productItem.available_on?.includes(this.currentState)) {
                // set the formatted product
                this.allProducts.push(productItem)
              }
            }

            // reference for next page
            if (Object.is(array.length - 1, key)) {
              this.lastProductCursor = product.cursor
            }
          })

          // current filter data from GraphQL response
          const filters = this.isSearchPage ? data.data.search.productFilters : data.data.collection.products.filters

          if (this.filterData && this.filterData.length > 0) {
            // check existing filter values and add any new filter item
            for (const filterData of this.filterData) {
              for (const filter of filters) {
                if (filterData.id === filter.id) {
                  for (const filterValues of filter.values) {
                    if (filterData.values.findIndex(filterProperty => filterProperty.id === filterValues.id) === -1) {
                      filterData.values.push(filterValues)
                    }
                  }
                }
              }
            }
          } else {
            // set default/initial filter values
            this.filterData = filters
          }

          if (this.hasNextPage) {
            this.showLoadMore = true // enable display of load more button

            // For "Featured" (manual) sort, don't show products until all are loaded and sorted
            // For other sorts, show initial products while the rest are being loaded
            const isFeaturedSort = this.sortBy?.sort_by === 'manual'

            if (!isFeaturedSort) {
              // show initial products while the rest of the collections are being loaded in the background
              if (!this.products?.length || this.products?.length < this.paginateBy || triggerFilter) {
                const sliceCount = this.paginateBy - this.products.length
                this.products = this.allProducts.slice(0, sliceCount)
                this.initFilterForm()
                // Don't clear loading state here - wait until all products are fetched (handled in else block)
              }
            } else {
              // For Featured sort, keep loading state until all products are fetched and sorted
              this.isLoading = true
            }

            // run the function again to get the next set of results
            await this.getProducts()
          } else {
            // Apply custom sorting for "Featured" (manual) option
            const wasSorted = this.sortBy?.sort_by === 'manual'
            if (wasSorted) {
              this._sortByFeatured()
            }

            // processed data
            // If we sorted, always update products to show first page of sorted order
            // Otherwise, only update if products array is empty or needs to be filled
            if (wasSorted) {
              // After sorting, always show first page of sorted products
              this.products = this.allProducts.slice(0, this.paginateBy)
            } else if (!this.products?.length || this.products?.length < this.paginateBy || triggerFilter) {
              const sliceCount = this.paginateBy - this.products.length
              this.products = this.allProducts.slice(0, sliceCount)
            }

            if (!this.activeSort) {
              this.activeSort = this.sortBy?.value
            }

            this.resultsCount = this.allProducts.length
            this.showLoadMore = this.resultsCount > this.products.length
            this.isLoadingMore = false

            this.initFilterForm()

            this.$nextTick(() => {
              this.toggleNewsletter(this.resultsCount) // toggle display of the newsletter section
            })

            // Clear loading state after all products are fetched (and sorted if Featured)
            setTimeout(() => {
              this.isLoading = false
            }, 1500)

            this.productsLoaded = false
            this.handleScroll()
          }

          if (data.errors) {
            this.logError(null, data.errors, config.url)
            return []
          }
        }).catch(error => {
          console.log('Storefront API Error:', error)
        }).finally(() => {
        })
      } catch (error) {
        this.logError(error)
      }
    },

    async getFilteredProducts (newRequest = false) {
      if (newRequest) {
        this.previewHasNextPage = null
        this.previewFilterQuery = []
        this.previewLastProductCursor = null
        this.previewAllProducts = []
        this.isResultsUpdating = true
      }

      // generate filter query
      if (this.filterData && this.filterData.length > 0) {
        for (const filter of this.filterData) {
          if (this.selectedFilters[filter.id]?.length) {
            if (filter.id !== 'filter.v.price') {
              const filterArray = this.selectedFilters[filter.id]
              for (const arrayElement of filterArray) {
                this.previewFilterQuery = this.previewFilterQuery.concat(JSON.parse(`${arrayElement}`))
              }
            }
          }
        }
      }
      // start of GraphQL query
      let productArguments = `first: 250, sortKey: ${this.sortBy.shopify_key}, reverse: ${this.sortBy.reverse}`

      // apply filter query
      if (this.previewFilterQuery && this.previewFilterQuery.length > 0) {
        let filterValues = ''
        for (const [index, query] of this.previewFilterQuery.entries()) {
          filterValues += `${JSON.stringify(query).replace(/"([^"]+)":/g, '$1:')}`
          if (index !== this.previewFilterQuery.length - 1) {
            filterValues += ','
          }
        }
        productArguments = this.isSearchPage ? productArguments + `, productFilters: [${filterValues}]` : productArguments + `, filters: [${filterValues}]`
      }

      // add last product index (for next page), sort value must be the same and there are no changes in the selected filters
      if (this.previewLastProductCursor) {
        productArguments = productArguments + `, after: "${this.previewLastProductCursor}"`
      }

      const metarequestBody = this.isSearchPage ? searchProductsRequest(this.searchQuery, productArguments) : collectionProductsRequest(this.collection.handle, productArguments)

      await axios.post(STOREFRONT_URL, metarequestBody, storefrontRequestConfig).then(async ({ data, config }) => {
        const productsFromShopify = this.isSearchPage ? data.data.search.edges : data.data.collection.products.edges
        this.previewHasNextPage = this.isSearchPage ? data.data.search.pageInfo?.hasNextPage : data.data.collection.products.pageInfo?.hasNextPage // this is true if collection has more than 250 products
        productsFromShopify.forEach((product, key, array) => {
          let productItem = {}
          productItem = transformGraphqlProduct(product.node)

          // check if the current state value is ACT
          if (this.currentState === 'ACT') {
            // If the state is ACT, include products from both ACT and NSW warehouses
            if (productItem.available_on?.includes(this.currentState) || productItem.available_on?.includes('NSW')) {
              // set the formatted product
              this.previewAllProducts.push(productItem)
            }
          } else {
            // check the state value (from the Cookie) and match if the selected state has an available inventory in the product
            if (productItem.available_on?.includes(this.currentState)) {
              // set the formatted product
              this.previewAllProducts.push(productItem)
            }
          }

          // reference for next page
          if (Object.is(array.length - 1, key)) {
            this.previewLastProductCursor = product.cursor
          }
        })

        if (this.previewHasNextPage) {
          this.getFilteredProducts()
        } else {
          this.filteredResultsCount = this.previewAllProducts?.length
          this.isResultsUpdating = false
        }
      }).catch(error => {
        console.log('Storefront API Error:', error)
      })
    },

    initFilterForm () {
      this.activeQuery = this.filterQuery

      for (const filter of this.filterData) {
        if (!(this.selectedFilters?.[filter.id])) {
          Vue.set(this.selectedFilters, filter.id, [])
        }
      }

      this._updateUrlParams()

      this.filterQuery = [] // reset previous query
    },

    handleClickLoadMore () {
      this.isLoadingMore = true
      this.currentPage += 1

      const moreProducts = this.allProducts.slice(this.numOfProducts, this.paginateBy * this.currentPage)
      // timeout can be removed, this is just added to gradually load the new items
      setTimeout(() => {
        this.products = [...this.products, ...moreProducts]
        this.isLoadingMore = false
      }, 750)
    },

    handleScroll () {
      const container = document.querySelector('.product-container')
      const trigger = document.querySelector('.load-more-trigger')

      if (!trigger) return

      const containerBottom = container.scrollTop + container.clientHeight
      const triggerTop = trigger.offsetTop

      if (triggerTop < containerBottom + 100 && !this.isLoadingMore && this.products.length < this.resultsCount) {
        this.handleClickLoadMore()
      }
    },

    clearAll () {
      // remove URL parameters before triggering reload
      const collectionURL = window.location.href.replace(window.location.search, '')
      window.location.href = collectionURL
    },

    /**
     * Custom sorting for "Featured" option
     * Sorts by ranking_priority (1 = highest, 10 = lowest), then by publishedAt (newest to oldest)
     * Products without ranking_priority are sorted last, then by publish date
     */
    _sortByFeatured () {
      this.allProducts.sort((a, b) => {
        const aPriority = a.ranking_priority
        const bPriority = b.ranking_priority

        // Helper to check if priority is valid (not null, not NaN, and is a number)
        const isValidPriority = (priority) => {
          return priority !== null && priority !== undefined && !isNaN(priority) && typeof priority === 'number'
        }

        const aHasPriority = isValidPriority(aPriority)
        const bHasPriority = isValidPriority(bPriority)

        // If both have valid ranking_priority, sort by priority (lower number = higher priority)
        if (aHasPriority && bHasPriority) {
          if (aPriority !== bPriority) {
            return aPriority - bPriority // Lower number comes first (1 before 2, etc.)
          }
          // If same priority, sort by publish date (newest first)
          const aDate = new Date(a.publishedAt)
          const bDate = new Date(b.publishedAt)
          return bDate - aDate // Newest first (descending)
        }

        // If only one has priority, prioritize the one with priority
        if (aHasPriority && !bHasPriority) {
          return -1 // a comes before b
        }
        if (!aHasPriority && bHasPriority) {
          return 1 // b comes before a
        }

        // If neither has priority, sort by publish date (newest first)
        const aDate = new Date(a.publishedAt)
        const bDate = new Date(b.publishedAt)
        return bDate - aDate // Newest first (descending)
      })
    },

    _setValuesFromUrlParams () {
      // Set selected filters based on URL params
      const url = window.location.toString()
      let params = url.split('?')[1]

      if (params) {
        this.filterQuery = [] // reset previous query
        if (params.includes('+&+')) {
          // workaround to prevent this value from splitting the parameters
          params = params.replaceAll('+&+', '_and_')
        }

        // build filter values
        const filters = params.includes('&') ? params.split('&') : [params]

        for (const filter of filters) {
          const filterElements = filter.split('=')
          const filterId = filterElements[0]
          // Decode URL-encoded filter value (e.g., %24500-%241000 becomes $500-$1000)
          // Handle special cases: + becomes space, _and_ becomes & (after decoding)
          const filterValue = filterElements[1] ? decodeURIComponent(filterElements[1]) : ''

          if (filterId.includes('p.m.')) {
            // product metafield filter
            const filterKey = filterId.split('.').pop()
            const metafieldValue = {
              productMetafield: {
                namespace: 'pdp',
                key: filterKey,
                value: filterValue
              }
            }

            this.filterQuery = this.filterQuery.concat(metafieldValue)

            if (this.selectedFilters[filterId]) {
              this.selectedFilters[filterId].push(JSON.stringify(metafieldValue))
            } else {
              this.selectedFilters[filterId] = [JSON.stringify(metafieldValue)]
            }
          } else if (filterId.includes('sort_by')) {
            this.sortBy = this.isSearchPage ? SHOPIFY_SEARCH_SORT_OPTIONS.find(option => option.label_key === filterValue) : SHOPIFY_SORT_OPTIONS.find(option => option.label_key === filterValue)
            this.sortValue = this.sortBy?.label_key
          }
        }
      }

      this.getProducts()
    }
  }
}
